import { Helmet } from 'react-helmet'
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { HashLink as Link } from 'react-router-hash-link'
import { motion, useAnimationControls } from 'framer-motion'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import MainHeader from '../../../components/MainHeader'
import MainFooter from '../../../components/MainFooter'
import CTAButton from '../../../components/CTAButton'
import AnimatedQuote from '../../../components/AnimatedQuote'
import PricingCard from '../../../components/PricingCard'
import { boxvar1, boxvar3, boxvar2_1, boxvar2_2, boxvar2dyndelay, oPU } from '../../../core.js'
import { faPhone, faChevronDown, faMagnifyingGlassArrowRight, faTags, faCheck } from '@fortawesome/free-solid-svg-icons'
import packageJson from '../../../../package.json'

import testimonial_klaus from '../../../assets/pictures/testimonials/testimonial_klaus.webp'
import macbook_webdev from '../../../assets/pictures/sectionimages/macbook_webdev-cut_compressed.webp'

import '../../Pricing/Pricing.css'
import '../../Pricing/MPricing.css'
import '../../Home/Home.css'
import '../../Home/MHome.css'
import './FunnelStartup.css'
import './MFunnelStartup.css'
import useProgressiveHeroBg from '../../../hooks/useProgressiveHeroBg'

const scrolldowniconanim = {
    idle: {
        y: [0, 6, 0],
        scale: 1,
        transition: { duration: 1.5, repeat: Infinity, ease: 'easeOut' },
    },
    hover: {
        y: [0, 3, 0],
        scale: 1.2,
        transition: { duration: 1.0, repeat: Infinity, ease: 'easeInOut' },
    },
}

const CALENDLY = 'https://calendly.com/tonetworkde-meet/getintouch'
const STAND = packageJson.pricingdate
const SITE = 'https://tonetwork.de'

const REGIONS = {
    memmingen: {
        city: 'Memmingen',
        region: 'Unterallgäu',
        canonical: '/landing/startup-memmingen',
        seoTitle: 'Webseite für Startups Memmingen – Web-Abo ab 59,99 €/Monat | ToNetwork',
        seoDescription: 'Professionelle Startup-Website in Memmingen: Web-Abo ab 59,99 €/Monat oder Einmalpakete ab 990 €. ToNetwork aus dem Unterallgäu – faire Konditionen für Gründer.',
        h1: 'Startup in Memmingen – professionelle Website, ohne Kapital zu verbrennen',
        h1sub: 'Web-Abo oder Einmalpaket – du entscheidest, wie du startest.',
        localLead: 'Wir begleiten Gründer in Memmingen und im Unterallgäu – mit fairen Konditionen, festem Ansprechpartner und Lösungen, die mit eurem Budget wachsen.',
        areaServed: ['Memmingen', 'Unterallgäu', 'Hawangen', 'Babenhausen', 'Türkheim'],
    },
    unterallgaeu: {
        city: 'Unterallgäu',
        region: 'Allgäu',
        canonical: '/landing/startup-unterallgaeu',
        seoTitle: 'Webseite für Startups Unterallgäu – Web-Abo & Pakete | ToNetwork',
        seoDescription: 'Startup-Website im Unterallgäu: Web-Abo ab 59,99 €/Monat oder Starter-Paket ab 990 €. Partner aus Hawangen – transparente Preise, kostenloses Erstgespräch.',
        h1: 'Gründen im Unterallgäu – online starten, Cashflow schonen',
        h1sub: 'Individuelle Webseite im monatlichen Abo – oder klassisch als Einmalprojekt.',
        localLead: 'Gerade am Anfang zählt jedes Euro – deshalb bieten wir Startups im Unterallgäu flexible Modelle statt starrer Agenturpreise.',
        areaServed: ['Unterallgäu', 'Memmingen', 'Türkheim', 'Mindelheim', 'Bad Wörishofen'],
    },
    allgaeu: {
        city: 'Allgäu',
        region: 'Schwaben',
        canonical: '/landing/startup-allgaeu',
        seoTitle: 'Webseite für Startups Allgäu – Web-Abo ab 59,99 € | ToNetwork',
        seoDescription: 'Startup-Website im Allgäu: Web-Abo, Starter- und Business-Pakete. ToNetwork – All-in-One Digitalisierung mit transparenten Listenpreisen für Gründer.',
        h1: 'Startup im Allgäu – digital von Tag eins',
        h1sub: 'Mehr Budget für Produkt und Marketing – die Website läuft im fairen Abo.',
        localLead: 'Ob Memmingen, Kempten oder das ländliche Allgäu: Ein Ansprechpartner für Webentwicklung, Hosting und Betreuung – persönlich und pragmatisch.',
        areaServed: ['Allgäu', 'Memmingen', 'Kempten', 'Unterallgäu', 'Oberallgäu'],
    },
    kempten: {
        city: 'Kempten',
        region: 'Allgäu',
        canonical: '/landing/startup-kempten',
        seoTitle: 'Webseite für Startups Kempten – Web-Abo & Pakete | ToNetwork',
        seoDescription: 'Startup-Website in Kempten: Web-Abo ab 59,99 €/Monat oder Einmalpakete ab 990 €. ToNetwork – persönlicher Partner aus dem Allgäu.',
        h1: 'Startup in Kempten & Allgäu – Webseite, die mitwächst',
        h1sub: 'Professioneller Auftritt ohne hohe Anfangsinvestition – Web-Abo oder Einmalzahlung.',
        localLead: 'Für Gründer in Kempten und der Region: kurze Wege, direkte Kommunikation und besonders faire Konditionen für Startups.',
        areaServed: ['Kempten', 'Allgäu', 'Memmingen', 'Sonthofen', 'Immenstadt'],
    },
    default: {
        city: 'Memmingen',
        region: 'Allgäu',
        canonical: '/funnel/startup',
        seoTitle: 'Webseite für Startups – Web-Abo & Pakete | ToNetwork',
        seoDescription: 'Startup-Funnel von ToNetwork: Web-Abo ab 59,99 €/Monat oder Einmalpakete (Starter ab 990 €, Business ab 2.190 €). Für Gründer in Memmingen, Unterallgäu und Allgäu.',
        h1: 'Dein Startup braucht eine Website – nicht eine Finanzierungsrunde dafür',
        h1sub: 'Web-Abo oder Einmalpaket – professionell online, fair kalkuliert.',
        localLead: 'Aus Hawangen im Unterallgäu begleiten wir Startups in Memmingen, im Allgäu und darüber hinaus – mit festem Ansprechpartner und Modellen, die zu knappem Budget passen.',
        areaServed: ['Memmingen', 'Unterallgäu', 'Allgäu', 'Kempten'],
    },
}

function resolveRegion(pathname) {
    if (pathname.includes('memmingen')) return REGIONS.memmingen
    if (pathname.includes('unterallgaeu')) return REGIONS.unterallgaeu
    if (pathname.includes('allgaeu')) return REGIONS.allgaeu
    if (pathname.includes('kempten')) return REGIONS.kempten
    return REGIONS.default
}

const webAboFeatures = [
    'Individuelle Webseite – maßgeschneidert entwickelt',
    'Hosting, Domain & SSL inklusive',
    'Mindestlaufzeit 24 Monate',
    'Einrichtungsgebühr 99 € (einmalig)',
    'Bis 2 Std. Wartung/Support pro Vertragsjahr inklusive',
    'Automatische Verlängerung um 6 Monate · Kündigung 3 Monate vor Laufzeitende',
]

const webStarter = {
    name: 'Starter',
    badge: 'Einmalig',
    price: 'ab 990 €',
    priceNote: 'einmalig',
    forwho: 'Vereine & kleine Auftritte',
    sales: 'Schnell online, professionell und bezahlbar – perfekt für Startups, die lieber einmal zahlen und die Seite behalten wollen.',
    features: [
        { t: 'bis 3 Seiten', in: null },
        { t: 'Design: Vorlage, individuell angepasst', in: null },
        { t: 'Mobiloptimiert', in: true },
        { t: 'SEO-Grundlagen', in: true },
        { t: 'Kontaktformular', in: false },
        { t: 'Blog / News', in: false },
        { t: 'Shop / Buchung / Login', in: false },
        { t: 'fertig in ~2 Wochen', in: null },
    ],
    paket: 'web-starter',
}

const webBusiness = {
    name: 'Business',
    badge: '🔥 Beliebt',
    featured: true,
    price: 'ab 2.190 €',
    priceNote: 'einmalig',
    forwho: 'KMU & Veranstalter',
    sales: 'Wenn du von Anfang an durchstarten willst: individuelles Design, SEO optimiert, Kontaktformular und Blog – gebaut, um Kunden zu gewinnen.',
    features: [
        { t: 'bis 6 Seiten', in: null },
        { t: 'Individuelles Design', in: null },
        { t: 'Mobiloptimiert', in: true },
        { t: 'SEO optimiert', in: true },
        { t: 'Kontaktformular', in: true },
        { t: 'Blog / News', in: true },
        { t: 'Shop / Buchung / Login', in: false },
        { t: '2 Revisionen · fertig in ~4 Wochen', in: null },
    ],
    paket: 'web-business',
}

const care = {
    name: 'Care',
    price: '39 €',
    priceNote: 'pro Monat',
    forwho: 'Grundabsicherung',
    sales: 'Für Einmalprojekte: Hosting, Sicherheit und Updates – monatlich kündbar, mit festem Ansprechpartner.',
    features: [
        { t: 'Hosting in Deutschland', in: true },
        { t: 'SSL & Sicherheits-Updates', in: true },
        { t: 'Monitoring (24/7)', in: true },
        { t: 'Regelmäßige Backups', in: false },
        { t: '.de Domain inklusive', in: false },
        { t: 'Kleine Änderungen inklusive', in: false },
        { t: 'Reaktionszeit innerhalb von 3 Werktagen (7-20 Uhr)', in: null },
        { t: 'Fester Ansprechpartner', in: true },
        { t: 'Monatlich kündbar', in: true },
    ],
    paket: 'care',
}

const webAboFaqs = [
    {
        q: 'Wie setzt sich der Preis zusammen?',
        a: 'Der Preis hängt vom Umfang deiner Ideen und der gewünschten Laufzeit ab (Mindestlaufzeit 24 Monate) und beginnt bei monatlich 59,99 €. Enthalten sind Hosting (standardmäßig 1 GB RAM, 1 vCore, bis zu 10 Mbit/s), Domain und komplette Softwareentwicklung. Wartung/Support für individuelle Funktionen: bis zu 2 Arbeitsstunden je Vertragsjahr inklusive – darüber hinaus Stundensatz. Einrichtungsgebühr: 99 € einmalig. Kostenpflichtige Assets (Bilder, Lizenzen etc.) stellst du bereit.',
    },
    {
        q: 'Was passiert nach Ende der Laufzeit?',
        a: 'Vor Vertragsende wählst du: Abo weiterlaufen lassen oder neues Abo mit komplett neuem Design. Mit Abo-Ende enden auch deine Nutzungsrechte an der Webseite (fremde Assets ausgeschlossen). Verlängerung automatisch um jeweils 6 Monate, sofern nicht anders vereinbart. Kündigungsfrist: 3 Monate vor Vertragsende.',
    },
    {
        q: 'Sind Änderungen während der Laufzeit möglich?',
        a: 'Ja. Zumutbare Rechtschreibkorrekturen, Logo-Tausch oder Öffnungszeiten-Anpassung sind kein Problem. Größere Änderungen (z. B. neuer Absatz) werden nach üblichem Stundensatz verrechnet – nicht über den Abopreis.',
    },
]

const painPoints = [
    {
        title: 'Hohe Agenturkosten fressen das Runway',
        text: 'Individuelle Webseiten kosten schnell mehrere Tausend Euro – Geld, das du in Produkt, Marketing oder Team investieren müsstest.',
    },
    {
        title: 'Billige Baukästen wirken unprofessionell',
        text: 'DIY-Lösungen sparen am Anfang, kosten aber später Zeit und Vertrauen. Investoren und erste Kunden googeln dich – der erste Eindruck zählt.',
    },
    {
        title: 'Technik frisst Gründer-Zeit',
        text: 'Hosting, SSL, Updates, Mobile-Optimierung – als Gründer willst du dein Produkt bauen, nicht WordPress debuggen.',
    },
]

const faqsForRegion = (city) => [
    {
        q: 'Web-Abo oder Einmalpaket – was passt zu meinem Startup?',
        a: 'Web-Abo (ab 59,99 €/Monat): wenig Kapitalbindung, alles inklusive – ideal bei knappem Budget und 24 Monate Planung. Einmalpaket Starter (ab 990 €) oder Business (ab 2.190 €): du zahlst einmal und behältst die Seite dauerhaft. Im Erstgespräch finden wir ehrlich heraus, was für dich passt.',
    },
    {
        q: `Arbeitet ihr auch mit Startups außerhalb von ${city}?`,
        a: 'Ja. Wir sitzen in Hawangen bei Memmingen und arbeiten mit Gründern im gesamten Unterallgäu, Allgäu und darüber hinaus – vor Ort oder per Video.',
    },
    {
        q: 'Gibt es Sonderkonditionen für Startups?',
        a: 'Ja. Für Startups, kleine Veranstalter und gemeinnützige Vereine gelten besonders faire Konditionen. Alle genannten Beträge sind Listenpreise ohne Rabatte – dein individuelles Angebot kann davon abweichen.',
    },
    {
        q: 'Brauche ich nach dem Einmalpaket noch Care-Betreuung?',
        a: 'Empfohlen, aber nicht Pflicht. Care (39 €/Monat) übernimmt Hosting, Sicherheit und Updates – monatlich kündbar. Im Web-Abo sind Hosting und grundlegende Wartung bereits enthalten.',
    },
]

function localBusinessJsonLd(region) {
    return {
        '@context': 'https://schema.org',
        '@type': 'ProfessionalService',
        name: 'ToNetwork UG (haftungsbeschränkt)',
        description: region.seoDescription,
        url: SITE,
        telephone: '+49-8332-4908872',
        email: 'hey@tonetwork.de',
        address: {
            '@type': 'PostalAddress',
            streetAddress: 'Hawangen',
            addressLocality: 'Hawangen',
            postalCode: '87749',
            addressRegion: 'Bayern',
            addressCountry: 'DE',
        },
        geo: {
            '@type': 'GeoCoordinates',
            latitude: 47.998,
            longitude: 10.283,
        },
        areaServed: region.areaServed.map((name) => ({ '@type': 'City', name })),
        priceRange: '€',
        knowsAbout: ['Webentwicklung', 'Startup-Website', 'Web-Abo', 'Hosting'],
    }
}

function WebAboCard({ delay }) {
    return (
        <motion.div
            variants={boxvar2dyndelay}
            custom={delay}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, margin: '-50px' }}
            className="pricing_card pricing_card-featured funnel_startup_abo_card"
        >
            <span className="pricing_badge">🚀 Für Startups</span>
            <h3 className="pricing_card_name">Web-Abo</h3>
            <span className="pricing_card_forwho">Monatlich statt Einmalinvestition</span>
            <div className="pricing_price">
                <span className="pricing_price_value">ab 59,99 €</span>
                <span className="pricing_price_note">pro Monat · zzgl. 99 € Einrichtung</span>
            </div>
            <p className="pricing_card_sales">
                Deine individuelle Webseite im günstigen Abo – Hosting, Domain und Entwicklung inklusive.
                So bleibt mehr Budget für Produkt und Wachstum.
            </p>
            <ul className="pricing_feat_list">
                {webAboFeatures.map((t) => (
                    <li key={t}>
                        <FontAwesomeIcon className="pricing_feat_icon pricing_feat_icon-in" icon={faCheck} />
                        <span>{t}</span>
                    </li>
                ))}
            </ul>
            <CTAButton
                className="pricing_card_cta"
                showArrow={true}
                title="Web-Abo anfragen"
                href="/support"
            />
        </motion.div>
    )
}

export default function FunnelStartup() {
    const { pathname } = useLocation()
    const region = resolveRegion(pathname)
    const canonicalUrl = SITE + region.canonical
    const faqs = faqsForRegion(region.city)

    const controls = useAnimationControls()

    useEffect(() => {
        controls.start('idle')
    }, [controls])

    useProgressiveHeroBg('sec_start--funnel-startup')

    return (
        <div className="mainCon funnel_startup" id="mainCon--funnel-startup">

            <MainHeader funnel disableanimation={false} />

            <Helmet>
                <meta charSet="utf-8" />
                <title>{region.seoTitle}</title>
                <link rel="canonical" href={canonicalUrl} />
                <meta name="description" content={region.seoDescription} />
                <meta name="keywords" content={`Startup Website ${region.city}, Web-Abo ${region.city}, Webdesign Startup ${region.region}, Gründer Website, ToNetwork`} />
                <meta property="og:title" content={region.seoTitle} />
                <meta property="og:description" content={region.seoDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:type" content="website" />
                <meta property="og:locale" content="de_DE" />
                <script type="application/ld+json">{JSON.stringify(localBusinessJsonLd(region))}</script>
            </Helmet>

            <section className="sec_start progressive-hero-bg" id="sec_start--funnel-startup">

                <motion.div
                    variants={boxvar1}
                    initial="hidden"
                    exit="hidden"
                    whileInView="visible"
                    viewport={{ once: false, margin: '-50px' }}
                    className="start_heading_cont"
                >
                    <AnimatedQuote className="start_quote" text={region.h1} />

                    <p className="start_about">
                        {region.h1sub}<br />
                        <br />
                        {region.localLead}<br />
                        <br />
                        Unser Tipp: <b className="start_about_highlight">Web-Abo ab 59,99 €/Monat</b> – oder Einmalpakete ab 990 €
                    </p>
                </motion.div>

                <motion.div variants={boxvar3} initial="hidden" exit="hidden" whileInView="visible" viewport={{ once: false, margin: '-50px' }}>
                    <div className="sec_start_cta_row">
                        <CTAButton className="sec_start_btn" showArrow={false} customIcon={faPhone} iconLeft={true} title="Kostenloses Erstgespräch" onclick={() => { oPU(CALENDLY) }} />
                        <CTAButton className="sec_start_btn sec_start_btn-chat" showArrow={true} lightcta={true} title="Web-Abo anfragen" href="/support" />
                    </div>
                </motion.div>

                <Link smooth to="#sec_next--funnel-startup">
                    <motion.div
                        className="sec_next_icon_div noselect"
                        onHoverStart={() => controls.start('hover')}
                        onHoverEnd={() => controls.start('idle')}
                    >
                        <span>Erfahre mehr</span>
                        <motion.div variants={scrolldowniconanim} animate={controls}>
                            <FontAwesomeIcon icon={faChevronDown} className="sec_next_icon_arrow" />
                        </motion.div>
                    </motion.div>
                </Link>

            </section>

            <section className="sec_next" id="sec_next--funnel-startup">
                <div className="sec_next_cont" id="sec_next--funnel-startup--cont">

                    <motion.div
                        variants={boxvar2_1}
                        className="funnel_startup_section"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <h2>Kommt dir das bekannt vor?</h2>
                        <h3>Drei Probleme, die Gründer in {region.city} und Umgebung kennen.</h3>
                        <div className="funnel_startup_pain_grid">
                            {painPoints.map((p, i) => (
                                <motion.div
                                    key={p.title}
                                    variants={boxvar2dyndelay}
                                    custom={0.15 * (i + 1)}
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: false, margin: '-50px' }}
                                    className="funnel_startup_pain_card"
                                >
                                    <span className="funnel_startup_pain_num">{String(i + 1).padStart(2, '0')}</span>
                                    <h4>{p.title}</h4>
                                    <p>{p.text}</p>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>

                    {/* Web-Abo */}
                    <motion.div
                        variants={boxvar2_1}
                        className="funnel_startup_section pricing_section"
                        id="funnel-startup-abo"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <h2>Die Lösung: Web-Abo</h2>
                        <h3>Individuelle Webseite – monatlich statt tausende Euro auf einmal.</h3>
                        <p className="pricing_lead">
                            Gerade für Startups können Projektkosten für eine individuelle Webseite schnell zum Problem werden.
                            Mit unserem Web-Abo erhältst du ab 59,99 € monatlich deine professionelle Website –
                            Hosting, Domain und Entwicklung inklusive. Mehr Geld bleibt für das, was dein Startup wirklich braucht.
                        </p>
                        <div className="pricing_grid funnel_startup_abo_grid">
                            <WebAboCard delay={0.15} />
                        </div>
                        <p className="pricing_note funnel_startup_abo_note">
                            Alle Preise exkl. MwSt. Es gelten die{' '}
                            <a href="/ToNetwork-Webabo-AGB-Jan24.pdf" target="_blank" rel="noopener noreferrer">Webabo-AGB</a>.
                            Der Abopreis ist abhängig vom Umfang – dein verbindliches Angebot erhältst du nach dem Erstgespräch.
                        </p>

                        {/* <div className="funnel_startup_abo_faqs">
                            <h3>Web-Abo im Detail</h3>
                            <div className="pricing_faq_list">
                                {webAboFaqs.map((f, i) => (
                                    <details key={i} className="pricing_faq_item" open={i === 0}>
                                        <summary>{f.q}</summary>
                                        <p>{f.a}</p>
                                    </details>
                                ))}
                            </div>
                        </div> */}
                    </motion.div>

                    {/* Einmalpakete */}
                    <motion.div
                        variants={boxvar2_1}
                        className="funnel_startup_section pricing_section"
                        id="funnel-startup-pakete"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <h2>Oder einmalig starten</h2>
                        <h3>Lieber investieren und behalten – klassische Pakete aus unserer Preisübersicht.</h3>
                        <p className="pricing_lead">
                            Wenn du von Anfang an weißt, dass du die Website dauerhaft brauchst und lieber einmal zahlst:
                            Unsere Starter- und Business-Pakete sind Listenpreise ohne Rabatte und werden individuell auf dein Projekt zugeschnitten.
                        </p>
                        <div className="pricing_grid">
                            <PricingCard tier={webStarter} delay={0.15} />
                            <PricingCard tier={webBusiness} delay={0.3} />
                        </div>
                        <p className="pricing_note">
                            Alle Preise netto, zzgl. MwSt. – Listenpreise ohne Rabatte. Stand: {STAND}
                        </p>
                    </motion.div>

                    {/* Care für Einmalprojekte */}
                    <motion.div
                        variants={boxvar2_1}
                        className="funnel_startup_section pricing_section"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <h2>Betreuung für dein Einmalprojekt</h2>
                        <h3>Hosting, Sicherheit und Updates – damit du dich auf dein Startup konzentrieren kannst.</h3>
                        <div className="pricing_grid funnel_startup_care_grid">
                            <PricingCard tier={care} delay={0.15} />
                        </div>
                        <p className="pricing_note">
                            Care ist monatlich kündbar. Im Web-Abo sind Hosting und grundlegende Wartung bereits enthalten.
                            Alle Preise netto, zzgl. MwSt. Stand: {STAND}
                        </p>
                        <br />
                        <CTAButton
                            showArrow={false}
                            customIcon={faTags}
                            iconLeft={true}
                            title="Alle weiteren Pakete & Preise"
                            href="/pricing"
                        />
                    </motion.div>

                    <motion.div
                        variants={boxvar2_2}
                        className="funnel_startup_section funnel_startup_why"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <div className="funnel_startup_why_text">
                            <h2>Warum ToNetwork?</h2>
                            <h3>Weil du bei uns alles bekommst – aus einer Hand.</h3>
                            <p>
                                Wir sind kein anonymer Online-Dienstleister, sondern dein persönlicher Partner aus dem Allgäu.
                                Webentwicklung, Hosting und Betreuung unter einem Dach – mit direkter Kommunikation
                                und dem Verständnis, dass Gründer anders ticken als etablierte KMU.
                            </p>
                            <ul className="funnel_startup_benefits">
                                <li>Web-Abo: wenig Kapitalbindung, alles inklusive</li>
                                <li>Besonders faire Konditionen für Startups</li>
                                <li>Hosting in Deutschland – DSGVO-konform</li>
                                <li>Erstberatung kostenlos, Festangebot ohne versteckte Kosten</li>
                                <li>Skalierbar – vom Starter-Paket bis zur Plattform</li>
                            </ul>
                            <CTAButton
                                showArrow={false}
                                customIcon={faMagnifyingGlassArrowRight}
                                iconLeft={true}
                                title="Alle Leistungen ansehen"
                                href="/services/webdev"
                            />
                        </div>
                        <img
                            src={macbook_webdev}
                            alt={`Webseite für Startups in ${region.city}`}
                            className="funnel_startup_why_img"
                            loading="lazy"
                        />
                    </motion.div>

                    <motion.div
                        variants={boxvar2_1}
                        className="funnel_startup_section"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <h2>Das sagen unsere Kunden</h2>
                        <div className="funnel_startup_testimonial">
                            <span className="funnel_startup_testimonial_quote">
                                „Mit ToNetwork haben wir einen langfristigen Partner für den Bereich Webentwicklung gefunden."
                            </span>
                            <div className="funnel_startup_testimonial_person">
                                <img src={testimonial_klaus} alt="Klaus Liepert, CEO it.liepert.gmbh" />
                                <div>
                                    <span className="funnel_startup_testimonial_name">Klaus Liepert</span>
                                    <span className="funnel_startup_testimonial_company">CEO, it.liepert.gmbh</span>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        variants={boxvar2_1}
                        className="funnel_startup_section pricing_section"
                        id="funnel-startup-faq"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <h2>Häufige Fragen</h2>
                        <div className="pricing_faq_list">
                            {faqs.map((f, i) => (
                                <details key={i} className="pricing_faq_item" open={i === 0}>
                                    <summary>{f.q}</summary>
                                    <p>{f.a}</p>
                                </details>
                            ))}
                        </div>
                    </motion.div>

                    <motion.div
                        variants={boxvar2dyndelay}
                        className="small_services_box small_services_box-bigbottom funnel_startup_cta_box"
                        initial="hidden"
                        whileInView="visible"
                        custom={0.3}
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <div className="small_services_textbox">
                            <h3>Bereit durchzustarten in {region.city}?</h3>
                            <br />
                            <p>
                                In einem kostenlosen 30-Minuten-Gespräch klären wir, ob Web-Abo oder Einmalpaket
                                für dich passt – ehrlich und ohne Verkaufsdruck. Alle Preise hier sind Listenpreise
                                ohne Rabatte und werden individuell für dich ausgestaltet.
                            </p>
                            <CTAButton
                                showArrow={false}
                                customIcon={faPhone}
                                iconLeft={true}
                                title="Kostenloses Erstgespräch"
                                onclick={() => { oPU(CALENDLY) }}
                            />
                            <br />
                            <p className="pricing_toni_hint">
                                Erst Orientierung gefragt? <a href="/chat">ToNi beantwortet Fragen zu Paketen und Preisen</a> – unverbindlich im Chat.
                            </p>
                        </div>
                    </motion.div>

                    <p className="pricing_footnote">
                        Es gelten unsere <a href="/rechtliches/agb">Allgemeinen Geschäftsbedingungen (AGB)</a>
                        {' '}sowie für das Web-Abo die{' '}
                        <a href="/ToNetwork-Webabo-AGB-Jan24.pdf" target="_blank" rel="noopener noreferrer">Webabo-AGB</a>.
                    </p>

                    <br /><br /><br />
                </div>
            </section>

            <MainFooter />
        </div>
    )
}
